import React, { useState, useRef } from 'react'
import { INTERAX_COLORS, INTERAX_TYPOGRAPHY } from '../../../../../constants/colors'

export interface SimpleTooltipProps {
  children: React.ReactNode
  content: string
  placement?: 'top' | 'bottom' | 'left' | 'right'
  className?: string
}

export const SimpleTooltip: React.FC<SimpleTooltipProps> = ({
  children,
  content,
  placement = 'top',
  className = ''
}) => {
  const [isVisible, setIsVisible] = useState(false)
  const [position, setPosition] = useState({ top: 0, left: 0 })
  const triggerRef = useRef<HTMLDivElement>(null)

  const handleMouseEnter = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect()
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop
      const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft

      let top = 0
      let left = 0

      switch (placement) {
        case 'top':
          top = rect.top + scrollTop - 40
          left = rect.left + scrollLeft + rect.width / 2
          break
        case 'bottom':
          top = rect.bottom + scrollTop + 8
          left = rect.left + scrollLeft + rect.width / 2
          break
        case 'left':
          top = rect.top + scrollTop + rect.height / 2
          left = rect.left + scrollLeft - 8
          break
        case 'right':
          top = rect.top + scrollTop + rect.height / 2
          left = rect.right + scrollLeft + 8
          break
      }

      setPosition({ top, left })
      setIsVisible(true)
    }
  }

  const handleMouseLeave = () => {
    setIsVisible(false)
  }

  const getTooltipClasses = () => {
    const baseClasses = 'absolute z-50 px-2 py-1 rounded text-xs whitespace-nowrap pointer-events-none'

    switch (placement) {
      case 'top':
        return `${baseClasses} transform -translate-x-1/2 -translate-y-full`
      case 'bottom':
        return `${baseClasses} transform -translate-x-1/2`
      case 'left':
        return `${baseClasses} transform -translate-x-full -translate-y-1/2`
      case 'right':
        return `${baseClasses} transform -translate-y-1/2`
      default:
        return baseClasses
    }
  }

  const getArrowClasses = () => {
    const baseClasses = 'absolute w-2 h-2 transform rotate-45'

    switch (placement) {
      case 'top':
        return `${baseClasses} bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2`
      case 'bottom':
        return `${baseClasses} top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2`
      case 'left':
        return `${baseClasses} right-0 top-1/2 transform translate-x-1/2 -translate-y-1/2`
      case 'right':
        return `${baseClasses} left-0 top-1/2 transform -translate-x-1/2 -translate-y-1/2`
      default:
        return baseClasses
    }
  }

  return (
    <>
      <div
        ref={triggerRef}
        className={`inline-block ${className}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </div>

      {isVisible && (
        <div
          className={getTooltipClasses()}
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
            backgroundColor: INTERAX_COLORS.white,
            border: `1px solid ${INTERAX_COLORS.redError}`,
            color: INTERAX_COLORS.offBlack,
            fontFamily: INTERAX_TYPOGRAPHY.body3.fontFamily,
            fontWeight: INTERAX_TYPOGRAPHY.body3.fontWeight,
            fontSize: INTERAX_TYPOGRAPHY.body3.fontSize,
            lineHeight: INTERAX_TYPOGRAPHY.body3.lineHeight
          }}
        >
          {content}
          <div
            className={getArrowClasses()}
            style={{
              backgroundColor: INTERAX_COLORS.white,
              borderRight: placement === 'left' ? `1px solid ${INTERAX_COLORS.redError}` : 'none',
              borderLeft: placement === 'right' ? `1px solid ${INTERAX_COLORS.redError}` : 'none',
              borderTop: placement === 'bottom' ? `1px solid ${INTERAX_COLORS.redError}` : 'none',
              borderBottom: placement === 'top' ? `1px solid ${INTERAX_COLORS.redError}` : 'none'
            }}
          />
        </div>
      )}
    </>
  )
}
